/* eslint-disable max-len */
import React, {
  useEffect,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useForm, FormProvider, FieldValues } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { IoTrash } from 'react-icons/io5';
import { GrUpdate } from 'react-icons/gr';
import styles from './general-infos.module.scss';

import {
  useGetEtaiement, useUpdateEtaiement, useGetEtaiementsList, useDeleteEtaiement,
  useUpdateEtaiementTransport,
} from '~/hooks/etaiements';

import {
  InputText,
  InputNumber,
  StyledSelect,
  ErrorField,
  Textarea,
  Checkbox,
} from '~/lib/HooksFormFields';
import { IPricing } from '~/types/agencies';
import PreviewField from '~/components/PreviewField';
import { IOption } from '~/types/options';
import withModalConfirm from '~/lib/withModalConfirm';
import { useUserProfile } from '~/hooks/auth';
import { formatDuration } from '~/utils';
import { createFile, deleteFile } from '~/api/files';
import type { IFile } from '~/types/files';
import type { IEtaiement } from '~/types/etaiements';
import { getPublicFileUrl, WORKSITE_PHOTO_MAX_BYTES } from '~/utils/files';
import ScopeMatrixForm from '~/components/etaiement/form/PhaseForm/ScopeMatrixForm';
import EtaiementAttachments from '~/components/etaiement/form/EtaiementAttachments';
import {
  resolveEtaiementScopeMatrix,
  sanitizeScopeMatrixForPersistence,
} from '~/utils/etaiement-scope-matrix';

const GeneralInfos = ({
  isPreview,
  confirm,
}: {
  isPreview: boolean,
  confirm: (message: string, action?: () => void,
    cancel?: () => void) => void
}) => {
  const { id } = useParams();
  const {
    data: etaiement = {
      agency: null,
      logistics: null,
      status: 'in-progress',
      updatedAt: null,
      hourlyRate: {
        price: null,
      },
      worksite: {
        city: '',
      },
      transport: {
        trucks: { maxSize: '', type: '' },
        discount: 0,
      },
    },
  } = useGetEtaiement(id || '');
  const etaiementSafe = etaiement as IEtaiement | null;

  const navigate = useNavigate();

  const { data: list = {} } = useGetEtaiementsList();
  const { data: userAuth } = useUserProfile();

  const {
    departments, discount, worksite, agencies,
  } = list;

  const operationalBases = useMemo(() => {
    const agency = agencies?.find((a: any) => a.value === etaiement?.agency?._id);
    if (agency) {
      return agency.pricing
        .sort((a: IPricing, b: IPricing) => a.location.localeCompare(b.location))
        .map((a: IPricing) => ({ label: a.location, value: a.location, ...a }));
    }
    return [];
  }, [agencies, etaiement]);

  const [worksitePhotoFile, setWorksitePhotoFile] = useState<IFile | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  const {
    mutate: updateEtaiement,
    mutateAsync: updateEtaiementAsync,
  } = useUpdateEtaiement('Les modifications ont été prise en compte');

  const {
    mutate: deleteEtaiement,
  } = useDeleteEtaiement('Le devis a été supprimé');

  const {
    mutate: updateEtaiementTransport,
  } = useUpdateEtaiementTransport('Les modifications ont été prise en compte');
  const methods = useForm<FieldValues>({
    mode: 'onBlur',
    defaultValues: {
      ...etaiement,
      scopeMatrix: resolveEtaiementScopeMatrix(etaiement as IEtaiement),
    },
  });
  const {
    control,
    watch,
    handleSubmit,
    reset,
  } = methods;

  const form: any = watch();

  useEffect(() => {
    if (!etaiementSafe?._id) return;
    const photo = etaiementSafe?.worksite?.photoFile;
    const photoId = typeof photo === 'object' && photo?._id ? photo._id : photo;
    reset({
      ...etaiementSafe,
      worksite: {
        ...etaiementSafe.worksite,
        photoFile: photoId || null,
      },
      scopeMatrix: resolveEtaiementScopeMatrix(etaiementSafe),
    });
    setWorksitePhotoFile(typeof photo === 'object' && photo?.path ? photo : null);
    setPhotoError(null);
  }, [etaiementSafe, reset]);

  const worksitePhotoPreviewUrl = useMemo(
    () => getPublicFileUrl(worksitePhotoFile),
    [worksitePhotoFile],
  );

  const handleWorksitePhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !id) return;

    setPhotoError(null);
    if (!file.type.startsWith('image/')) {
      setPhotoError('Le fichier doit être une image.');
      return;
    }
    if (file.size > WORKSITE_PHOTO_MAX_BYTES) {
      setPhotoError('La photo ne doit pas dépasser 1 Mo.');
      return;
    }

    const previousPhotoId = worksitePhotoFile?._id
      || (typeof etaiementSafe?.worksite?.photoFile === 'string' ? etaiementSafe.worksite.photoFile : null);

    setIsUploadingPhoto(true);
    try {
      const { file: uploaded } = await createFile(file);
      await updateEtaiementAsync({
        _id: id,
        worksite: {
          ...form?.worksite,
          photoFile: uploaded._id,
        },
      });
      setWorksitePhotoFile(uploaded);
      if (previousPhotoId && previousPhotoId !== uploaded._id) {
        try {
          await deleteFile(previousPhotoId);
        } catch {
          // ignore orphan cleanup failure
        }
      }
    } catch {
      setPhotoError('Impossible d\'enregistrer la photo.');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleRemoveWorksitePhoto = async () => {
    if (!id || !worksitePhotoFile?._id) return;
    const photoId = worksitePhotoFile._id;
    setIsUploadingPhoto(true);
    try {
      await updateEtaiementAsync({
        _id: id,
        worksite: {
          ...form?.worksite,
          photoFile: null,
        },
      });
      setWorksitePhotoFile(null);
      try {
        await deleteFile(photoId);
      } catch {
        // ignore
      }
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const renderWorksitePhotoContent = () => {
    if (isPreview) {
      if (worksitePhotoPreviewUrl) {
        return (
          <img
            src={worksitePhotoPreviewUrl}
            alt="Photo du chantier"
            className={styles.worksitePhotoPreview}
          />
        );
      }
      return <p className={styles.worksitePhotoEmpty}>Aucune photo</p>;
    }

    const addPhotoLabel = worksitePhotoPreviewUrl ? 'Remplacer la photo' : 'Ajouter une photo';

    return (
      <>
        {worksitePhotoPreviewUrl && (
          <div className={styles.worksitePhotoPreviewWrap}>
            <img
              src={worksitePhotoPreviewUrl}
              alt="Photo du chantier"
              className={styles.worksitePhotoPreview}
            />
            <button
              type="button"
              className={styles.removePhoto}
              onClick={handleRemoveWorksitePhoto}
              disabled={isUploadingPhoto}
            >
              Supprimer la photo
            </button>
          </div>
        )}
        <label className={styles.addPhoto} htmlFor="worksite-photo-input">
          {addPhotoLabel}
        </label>
        <input
          id="worksite-photo-input"
          type="file"
          accept="image/*"
          onChange={handleWorksitePhotoChange}
          disabled={isUploadingPhoto}
        />
        {photoError && <ErrorField message={photoError} />}
      </>
    );
  };

  const isDeleteEtaiement = userAuth?.role ? (userAuth?.role === 'Admin' || userAuth?._id === form.author._id) : false;

  const discountEmail = userAuth?.referrer1?.email || userAuth?.referrer2?.email;

  const operationalBasePrice = useMemo(() => {
    const prices: any = {

    };
    const base = operationalBases?.find(
      (o: any) => o.value === form?.logistics?.operationalBase,
    );
    if (base?.hourlyRate) {
      prices.hourlyRate = base?.hourlyRate;
    }
    const transportType = form?.transport?.trucks.type;
    const maxSize = form?.transport?.trucks.maxSize;
    if (base?.trucks && transportType) {
      const transportPrice6m = base?.trucks.find((t: any) => {
        const { key } = t;
        if (key === 'PL-6M' && transportType === 'sans grue') {
          return t;
        }
        if (key === 'PL-6MG' && transportType === 'avec grue') {
          return t;
        }
        return null;
      })?.price;
      if (transportPrice6m) {
        prices.truck6m = transportPrice6m;
      }
      const transportPriceT7 = base?.trucks.find((t: any) => {
        const { key } = t;
        if (key === 'T7-max-12T' && transportType === 'sans grue') {
          return t;
        }
        return null;
      })?.price;
      if (transportPriceT7) {
        prices.truckT7 = transportPriceT7;
      }
      const transportPrice12m = base?.trucks.find((t: any) => {
        const { key } = t;
        if (key === 'PL-13M' && transportType === 'sans grue') {
          return t;
        }
        if (key === 'PL-12-MG' && transportType === 'avec grue') {
          return t;
        }
        return null;
      })?.price;
      if (transportPrice12m && maxSize === '12m') {
        prices.truck12m = transportPrice12m;
      }
    }
    return prices;
  }, [form, operationalBases]);

  const handleDelete = () => {
    if (isPreview) return;
    confirm(
      'Êtes-vous sûr de vouloir supprimer ce devis?',
      async () => {
        await deleteEtaiement({
          etaiementId: id || '',
        });
        navigate('/estimates/etaiements');
        const layout = document?.getElementById('layout');
        if (layout) {
          layout.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      },
      () => null,
    );
  };

  const contactsOptions = useMemo(() => form?.client?.contacts.map((c: any) => ({ value: c?._id, label: `${c.firstName} ${c?.lastName}` })), [form?.client]);

  const submit = useCallback(async (data: FieldValues) => {
    await updateEtaiement({
      _id: id,
      ...data,
      scopeMatrix: {
        rows: sanitizeScopeMatrixForPersistence(data.scopeMatrix?.rows),
      },
    });
  }, [id, updateEtaiement]);

  const debouncedSubmit = useDebounceCallback(submit, 1500);

  const submitOnChange = () => watch(() => {
    handleSubmit(debouncedSubmit)();
  });

  useEffect(() => {
    const subscription = submitOnChange();
    return () => subscription.unsubscribe();
  }, [submitOnChange]);

  return (
    <FormProvider {...methods}>
    <div className={styles.generalInfos}>
      <section>
        <h4>Information sur le chantier</h4>
        <div className={styles.row}>
          <div className={styles.containerField}>
            {isPreview
              ? <PreviewField
                label='Nom du chantier'
                value={form?.worksite?.name}
              />
              : <InputText
                name='worksite.name'
                label='Nom du chantier'
                control={control}
              />
            }
            {!form?.worksite?.name && <ErrorField message="le nom du chantier est requis" />}
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.containerField}>
            {isPreview
              ? <PreviewField
                label='Ville'
                value={form?.worksite?.city}
              />
              : <InputText
                name='worksite.city'
                label='Ville'
                control={control}
              />
            }
            {!form?.worksite?.city && <ErrorField message="la ville est requise" />}
          </div>
          <div className={styles.containerField}>
            {isPreview
              ? <PreviewField
                label='Code postal du chantier'
                value={form?.worksite?.name}
              />
              : <InputText
                name='worksite.postalCode'
                label='Code postal du chantier'
                control={control}
              />
            }
            {!form?.worksite?.postalCode && <ErrorField message="le code postal est requis" />}
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.containerField}>
            {isPreview
              ? <PreviewField
                label='Adresse'
                value={form?.worksite?.address}
              />
              : <InputText
                name='worksite.address'
                label='Adresse'
                control={control}
              />
            }
          </div>
          <div className={styles.containerField}>
            {isPreview
              ? <PreviewField
                label='Date démarrage prévisionnelle'
                value={form?.worksite?.estimatedStartDate ? format(new Date(), 'dd/MM/yyyy') : '---'}
              />
              : <InputText
                name='worksite.estimatedStartDate'
                type='date'
                label='Date démarrage prévisionnelle'
                control={control}
              />
            }
          </div>
        </div>
        <div className={styles.containerField}>
          {isPreview
            ? <PreviewField
              label="Complément d'adresse"
              value={form?.worksite?.address2}
            />
            : <InputText
              name='worksite.address2'
              label="Complément d'adresse"
              control={control}
            />
          }
        </div>
        <div className={styles.row}>
          <div className={styles.containerField}>
            {isPreview
              ? <PreviewField
                label='Département'
                value={departments?.find((d: IOption) => d.value === form?.worksite?.department)?.label || '---'}
              />
              : <StyledSelect
                name='worksite.department'
                label='Département'
                isSearchable
                control={control}
                options={departments}
              />
            }
            {!form?.worksite?.department && <ErrorField message="le département est requis" />}
          </div>
        </div>
        <div className={styles.containerField}>
          {isPreview
            ? <PreviewField
              label='Description du chantier'
              value={form?.worksite.description || '---'}
            />
            : <Textarea
              name='worksite.description'
              label='Description du chantier'
              control={control}
            />
          }
        </div>
        <div className={styles.containerField}>
          {isPreview
            ? <PreviewField
              label='Informations diverses (n’apparait pas sur le devis)'
              value={form?.worksite.infos || '---'}
            />
            : <Textarea
              name='worksite.infos'
              label='Informations diverses (n’apparait pas sur le devis)'
              control={control}
            />
          }
        </div>
        <div className={`${styles.containerField} ${styles.worksitePhoto}`}>
          <label>Photo du chantier (max. 1 Mo)</label>
          {renderWorksitePhotoContent()}
        </div>
      </section>
      <hr />
      <section>
        <EtaiementAttachments
          etaiementId={id || ''}
          files={etaiementSafe?.files || []}
          isPreview={isPreview}
        />
      </section>
      <hr />
      <section>
        <ScopeMatrixForm isPreview={isPreview} />
      </section>
      <hr />
      <section>
        <h4>Contact côté client</h4>
        <div className={styles.row}>
          <div className={styles.containerField}>
            {isPreview
              ? <PreviewField
                label='Nom du contact'
                value={contactsOptions.find((c: any) => c === form?.contact)?.label}
              />
              : <>
                <StyledSelect
                  name='contact'
                  label='Nom du contact'
                  control={control}
                  options={contactsOptions}
                />
                {!form?.contact && <ErrorField message="le contact est requis" />}
              </>
            }
          </div>
        </div>
      </section>
      <hr />
      <section>
        <h4>Main d’oeuvre</h4>
        <div className={styles.row}>
          <div className={styles.containerField}>
            {isPreview
              ? <PreviewField
                label='Destination'
                value={operationalBases?.find((d: IOption) => d.value === form?.logistics?.operationalBase)?.label || '---'}
              />
              : <>
                <StyledSelect
                  name='logistics.operationalBase'
                  label='Destination'
                  control={control}
                  isSearchable
                  options={operationalBases}
                />
                {form?.logistics?.operationalBase && !operationalBasePrice?.hourlyRate && <ErrorField message="la destination est incompléte" />}
                {!form?.logistics?.operationalBase && <ErrorField message="la destination est requise" />}
              </>
            }
          </div>
          <div className={`${styles.rate} ${form?.logistics?.discountMO > 0 ? styles.discount : ''}`}>
            <p>
              {(etaiement?.logistics?.calculatedPriceUsed === false
                || (operationalBasePrice?.hourlyRate
                  && (etaiement?.hourlyRate && !etaiement?.hourlyRate?.price)))
                && <>
                  Taux horaire : <span>{operationalBasePrice?.hourlyRate} €</span>{' '}
                  {form?.logistics?.discountMO > 0 && <span>
                    {(
                      operationalBasePrice.hourlyRate * (1 - form.logistics.discountMO)
                    ).toFixed(2)} €
                  </span>}<br />
                </>
              }
              {etaiement?.hourlyRate
                && etaiement?.hourlyRate?.price
                && etaiement?.logistics?.calculatedPriceUsed === true
                && <>
                    Taux horaire : <span>
                      {form?.logistics.calculatedPriceUsed === true
                        ? etaiement?.hourlyRate?.price?.toFixed(2)
                        : operationalBasePrice?.hourlyRate.toFixed(2)} €
                    </span>{' '}
                    {form?.logistics?.discountMO > 0 && <span>
                      {(etaiement.hourlyRate.price * (1 - form.logistics.discountMO)).toFixed(2)} €
                    </span>}
                  </>
              }
            </p>
            {operationalBasePrice?.hourlyRate
              && (etaiement?.hourlyRate && etaiement?.hourlyRate?.price)
              && (
                <Checkbox
                  name='logistics.calculatedPriceUsed'
                  label='Utiliser le taux calculé'
                  control={control}
                  disabled={isPreview}
                />
              )
            }
          </div>
        </div>
        {etaiement?.worksite?.city && etaiement?.agency && id
          && <div className={styles.transport}>
            <div className={styles['transport-details']}>
              <div className={styles.col}>
                <p>{etaiement?.agency?.address?.city} - {etaiement?.worksite?.city}</p>
              </div>
              <div className={styles.col}>
                <p>{etaiement?.hourlyRate?.km?.toFixed() || '---'} km</p>
              </div>
              <div className={styles.col}>
                <p>{etaiement?.hourlyRate?.minutes ? formatDuration(etaiement?.hourlyRate.minutes) : '---'}</p>
              </div>
              <div className={styles.col}>
                <p>{etaiement?.hourlyRate?.price?.toFixed(2) || '---'} €</p>
              </div>
              <button
                onClick={async () => {
                  await updateEtaiementTransport({ etaiementId: id }, {
                    onSuccess: (response) => {
                      if (response?.etaiement) {
                        reset(response.etaiement);
                      }
                    },
                  });
                }}
              >
                {GrUpdate({})}
              </button>
            </div>
            {etaiement?.hourlyRate?.error && <ErrorField message={"Une des villes n'as pas été trouvée"} />}
          </div>
        }
        <div className={styles.row}>
          <div className={`${styles.containerField} ${!isPreview ? styles.discount : ''}`}>
            {isPreview
              || (form?.logistics?.discountMO
                && !discount?.workforce
                  .find((d: IOption) => d.value === form?.logistics?.discountMO))
              ? <PreviewField
                label='Remise sur la MO'
                className='discount'
                value={form?.logistics?.discountMO ? `${form.logistics.discountMO * 100} %` : '---'}
              />
              : <StyledSelect
                name='logistics.discountMO'
                label='Remise sur la MO'
                control={control}
                className={'discount'}
                discountEmail={discountEmail}
                estimateId={id || ''}
                discountDocumentSegment="etaiements"
                options={discount?.workforce}
              />
            }
          </div>
        </div>
      </section>
      <hr />
      <section>
        <h4>Location</h4>
        <div className={styles.row}>
          <div className={`${styles.containerField} ${!isPreview ? styles.discount : ''}`}>
            {isPreview
              || (form?.location?.discount
                && !discount?.location.find((d: IOption) => d.value === form?.location?.discount))
              ? <PreviewField
                label='Remise sur la location'
                className='discount'
                value={form?.location?.discount ? `${form.location.discount * 100} %` : '---'}
              />
              : <StyledSelect
                name='location.discount'
                label='Remise sur la location'
                control={control}
                className={'discount'}
                discountEmail={discountEmail}
                estimateId={id || ''}
                discountDocumentSegment="etaiements"
                options={discount?.location}
              />
            }
          </div>
          <div className={`${styles.containerField} ${styles.duration}`}>
            {isPreview
              ? <PreviewField
                label='Durée globale du chantier'
                value={form?.worksite?.duration ? `${form?.worksite?.duration} mois` : '---'}
              />
              : <>
                <label>Durée globale du chantier</label>
                <div className={styles.fields}>
                  <InputNumber
                    name='worksite.duration'
                    control={control}
                  />
                </div>
              </>
            }
          </div>
        </div>
        <div className={`${styles.containerField} ${styles.checkbox}`}>
          <Checkbox
            name='location.globalPrice'
            label='Appliquer un tarif global'
            control={control}
            disabled={isPreview}
          />
        </div>
        {(form?.location?.globalPrice && (() => {
          const globalRentalUnitPrice = Number(
            form?.phases?.[0]?.rentals?.[0]?.unitPrice
            ?? form?.phases?.[0]?.rental?.unitPrice
            ?? 0,
          );
          return globalRentalUnitPrice > 0;
        })())
            && <div className={styles.row}>
              <p className={`${styles.price} ${form?.location?.discount && form?.location?.discount > 0 ? styles.discount : ''}`}>
                Prix unitaire : <span>{
                  form?.phases?.[0]?.rentals?.[0]?.unitPrice
                  ?? form?.phases?.[0]?.rental?.unitPrice
                } €</span>
                {' '}{form?.location?.discount && form?.location?.discount > 0 && (() => {
                  const globalRentalUnitPrice = Number(
                    form?.phases?.[0]?.rentals?.[0]?.unitPrice
                    ?? form?.phases?.[0]?.rental?.unitPrice
                    ?? 0,
                  );
                  return globalRentalUnitPrice > 0 ? (
                    <span>
                      {(globalRentalUnitPrice * (1 - form.location.discount)).toFixed(2)} €
                    </span>
                  ) : null;
                })()}
              </p>
            </div>
          }
      </section>
      <hr />
      <section>
        <h4>Transport</h4>
        <div className={styles.row}>
          <div className={styles.containerField}>
            {isPreview
              ? <PreviewField
                label='Taille maximale des camions'
                value={worksite?.trucksMaxSizes?.find((d: IOption) => d.value === form?.transport?.trucks?.maxSize)?.label || '---'}
              />
              : <StyledSelect
                name='transport.trucks.maxSize'
                label='Taille maximale des camions'
                placeholder='Taille maximale des camions'
                control={control}
                options={worksite?.trucksMaxSizes || []}
              />
            }
          </div>
          <div className={styles.containerField}>
            {isPreview
              ? <PreviewField
                label='Type de camion'
                value={worksite?.trucksTypes?.find((d: IOption) => d.value === form?.transport?.trucks?.type)?.label || '---'}
              />
              : <StyledSelect
                name='transport.trucks.type'
                label='Type de camion'
                placeholder='Type de camion'
                control={control}
                options={worksite?.trucksTypes || []}
              />
            }
          </div>
        </div>
        <div className={styles.row}>
          <div className={`${styles.containerField} ${!isPreview ? styles.discount : ''}`}>
            {isPreview
              || (form?.transport?.discount
                && !discount?.transport.find((d: IOption) => d.value === form?.transport?.discount))
              ? <PreviewField
                label='Remise sur les transports'
                className='discount'
                value={form?.transport?.discount ? `${form.transport.discount * 100} %` : '---'}
              />
              : <StyledSelect
                name='transport.discount'
                label='Remise sur les transports'
                control={control}
                className={'discount'}
                discountEmail={discountEmail}
                estimateId={id || ''}
                discountDocumentSegment="etaiements"
                options={discount?.transport}
              />
            }
          </div>
        </div>
        {operationalBasePrice?.truckT7
          && <p className={`${styles.rate} ${form?.transport?.discount > 0 ? styles.discount : ''}`}>
            Taux transport T7 : <span>{operationalBasePrice.truckT7} €</span>{' '}{form?.transport?.discount > 0 && <span>{(operationalBasePrice.truckT7 * (1 - form.transport.discount)).toFixed(2)} €</span>}
          </p>
        }
        {operationalBasePrice?.truck6m
          && <p className={`${styles.rate} ${form?.transport?.discount > 0 ? styles.discount : ''}`}>
            Taux transport 6m : <span>{operationalBasePrice.truck6m} €</span>{' '}{form?.transport?.discount > 0 && <span>{(operationalBasePrice.truck6m * (1 - form.transport.discount)).toFixed(2)} €</span>}
          </p>
        }
        {operationalBasePrice?.truck12m
          && <p className={`${styles.rate} ${form?.transport?.discount > 0 ? styles.discount : ''}`}>
            Taux transport 12m : <span>{operationalBasePrice.truck12m} €</span>{' '}{form?.transport?.discount > 0 && <span>{(operationalBasePrice.truck12m * (1 - form.transport.discount)).toFixed(2)} €</span>}
          </p>
        }
      </section>
      {!isPreview && !form.sendAt && isDeleteEtaiement
        && <button
          type="button"
          className={styles.delete}
          onClick={() => handleDelete()}
        >
          {IoTrash({})} Supprimer le devis
        </button>
      }
    </div>
    </FormProvider>
  );
};

export default withModalConfirm(GeneralInfos);
